import apiClient from './apiClient'
import { Conversation, Message } from '../types/conversation.types'

export interface ConversationFilters {
  bot_session_id?: number
  status?: string
  search?: string
  page?: number
  limit?: number
  include_finished?: boolean
  /** IDs separados por vírgula (ex.: `1,3,5`). */
  tag_ids?: string
  tag_match?: 'any' | 'all'
  /** Incluir array `tags` em cada conversa. */
  include_tags?: boolean
}

export const conversationService = {
  async getConversations(
    filters: ConversationFilters = {},
    signal?: AbortSignal
  ): Promise<{ success: boolean; data: { conversations: Conversation[]; pagination: any } }> {
    const response = await apiClient.get('/conversations', { params: filters, signal })
    return response.data
  },

  async getConversation(
    id: number,
    options?: { include_tags?: boolean }
  ): Promise<{ success: boolean; data: Conversation }> {
    const params = options?.include_tags ? { include_tags: true } : undefined
    const response = await apiClient.get(`/conversations/${id}`, { params })
    return response.data
  },

  async getMessages(conversationId: number, page = 1, limit = 50): Promise<{ success: boolean; data: { messages: Message[]; pagination: any } }> {
    const response = await apiClient.get(`/conversations/${conversationId}/messages`, { 
      params: { page, limit } 
    })
    return response.data
  },

  async sendMessage(conversationId: number, messageText: string, messageType: string = 'text'): Promise<{ success: boolean; data: Message }> {
    const response = await apiClient.post(`/conversations/${conversationId}/messages`, {
      message_text: messageText,
      message_type: messageType,
      sender_type: 'bot'
    })
    return response.data
  },

  async updateConversationStatus(id: number, status: string): Promise<{ success: boolean; data: Conversation }> {
    const response = await apiClient.put(`/conversations/${id}`, { status })
    return response.data
  },

  async finishConversation(id: number): Promise<{ success: boolean; data?: Conversation; message?: string }> {
    const response = await apiClient.post(`/conversations/${id}/finish`)
    return response.data
  },

  async archiveConversation(id: number): Promise<{ success: boolean; data?: Conversation; message?: string }> {
    const response = await apiClient.post(`/conversations/${id}/archive`)
    return response.data
  },

  async resumeConversation(id: number): Promise<{ success: boolean; data?: Conversation; message?: string }> {
    const response = await apiClient.post(`/conversations/${id}/resume`)
    return response.data
  },

  async assumeConversation(id: number): Promise<{ success: boolean; data?: Conversation; message?: string }> {
    const response = await apiClient.post(`/conversations/${id}/assume`)
    return response.data
  },

  async transferConversation(
    id: number,
    toAttendantId: number,
    note?: string
  ): Promise<{ success: boolean; data?: Conversation; message?: string }> {
    const payload: any = { toAttendantId }
    if (note && note.trim()) {
      payload.note = note.trim()
    }
    const response = await apiClient.post(`/conversations/${id}/transfer`, payload)
    return response.data
  },

  async markTransferNoteRead(
    conversationId: number,
    transferId: number
  ): Promise<{ success: boolean; message?: string }> {
    const response = await apiClient.post(
      `/conversations/${conversationId}/transfers/${transferId}/note-read`
    )
    return response.data
  }
}
